import React, { useState, useEffect } from 'react';
import Axios from 'axios';
import { HelpCircle, CheckCircle, ChevronRight, MessageSquare } from 'lucide-react';
import CircularProgress from '@mui/material/CircularProgress';

export default function Support() {
    const backEndUrl = process.env.REACT_APP_SERVER_ADDRESS;
    const [data, setData] = useState(null);
    const [subject, setSubject] = useState('');
    const [problem, setProblem] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        Axios({
            method: "GET",
            withCredentials: true,
            url: `${backEndUrl}/auth/consultantLog`,
        }).then((res) => {
            setData(res.data);
        });
    }, []);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!subject.trim() || !problem.trim()) {
            setError('Please fill in all fields before submitting.');
            return;
        }
        setIsSubmitting(true);
        setError('');
        Axios({
            method: "POST",
            data: {
                subject,
                problem,
                consultantEmail: data.email,
            },
            withCredentials: true,
            url: `${backEndUrl}/users/support`,
        }).then((res) => {
            if (res.data === 'success') {
                setSubmitted(true);
                setSubject('');
                setProblem('');
            } else {
                setError('Something went wrong. Please try again.');
            }
        }).catch(() => {
            setError('Something went wrong. Please try again.');
        }).finally(() => {
            setIsSubmitting(false);
        });
    };

    if (!data) {
        return (
            <div className="flex items-center justify-center h-64">
                <CircularProgress size={28} />
            </div>
        );
    }

    return (
        <div className="px-10 pb-10">
            <div className="mt-8">

                {/* Breadcrumb */}
                <nav className="hidden sm:flex items-center gap-1 text-sm text-gray-500 mb-2">
                    <span>Dashboard</span>
                    <ChevronRight className="w-4 h-4 shrink-0" />
                    <span className="text-gray-700 font-medium">Support</span>
                </nav>

                <div className="mt-2 md:flex md:items-center md:justify-between mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 sm:text-3xl sm:tracking-tight">
                        Support
                    </h2>
                </div>

                <div className="space-y-5">

                    {submitted ? (
                        <div className="bg-white rounded-xl shadow-sm p-12 flex flex-col items-center text-center gap-4">
                            <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center">
                                <CheckCircle className="w-7 h-7 text-green-500" />
                            </div>
                            <h3 className="text-xl font-bold text-slate-800">Ticket Submitted!</h3>
                            <p className="text-slate-500 text-sm max-w-xs">
                                We've received your message and will get back to you as soon as possible.
                            </p>
                            <button
                                onClick={() => setSubmitted(false)}
                                className="mt-2 text-sm text-[#0a79be] font-medium hover:underline"
                            >
                                Submit another request
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit}>
                            <div className="bg-white rounded-xl shadow-sm overflow-hidden">

                                {/* Card header */}
                                <div className="px-6 py-5 border-b border-slate-100 flex items-center gap-3">
                                    <div className="w-9 h-9 rounded-lg bg-sky-50 flex items-center justify-center">
                                        <HelpCircle className="w-5 h-5 text-sky-600" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-slate-800 text-sm">Submit a Support Ticket</h3>
                                        <p className="text-xs text-slate-400">We typically respond within 1 business day</p>
                                    </div>
                                </div>

                                {/* Form body */}
                                <div className="px-6 py-6 space-y-5">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                                            Subject
                                        </label>
                                        <input
                                            type="text"
                                            value={subject}
                                            onChange={(e) => setSubject(e.target.value)}
                                            placeholder="e.g. Issue with document upload"
                                            className="w-full  border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-800 placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                                            Message
                                        </label>
                                        <textarea
                                            value={problem}
                                            onChange={(e) => setProblem(e.target.value)}
                                            rows={6}
                                            placeholder="Describe your issue or question in as much detail as possible..."
                                            className="w-full  border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-800 placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition resize-none"
                                        />
                                    </div>

                                    {error && (
                                        <p className="text-sm text-red-500">{error}</p>
                                    )}
                                </div>

                                {/* Card footer */}
                                <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end">
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="inline-flex items-center gap-2 bg-[#0a79be] hover:bg-[#0052CC] text-white px-5 py-2 rounded-lg text-sm font-semibold shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isSubmitting && <CircularProgress size={14} color="inherit" />}
                                        {isSubmitting ? 'Sending…' : 'Send Message'}
                                    </button>
                                </div>
                            </div>
                        </form>
                    )}

                    {/* Helper tip */}
                    <div className="bg-white rounded-xl shadow-sm p-5 flex items-start gap-4">
                        <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                            <MessageSquare className="w-4 h-4 text-slate-500" />
                        </div>
                        <div>
                            <p className="text-sm font-semibold text-slate-700">Tips for a faster response</p>
                            <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                                Include any relevant client names, file names, or error messages to help us resolve your issue quickly.
                            </p>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}
